import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useMutation, useQuery } from '@tanstack/react-query';
import { lessonService } from '../../services/lessonService';
import { useLessonStore } from '../../store/lessonStore';
import { SlideExportDialog } from '../../components/slides/SlideExportDialog';

interface Phase4Props {
  lessonId: string;
  readOnly?: boolean;
}

export const Phase4Assembly: React.FC<Phase4Props> = ({ lessonId, readOnly = false }) => {
  const { currentVersion, currentLesson } = useLessonStore();
  const versionNumber = currentVersion?.version_number ?? 1;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [showNotes, setShowNotes] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const { data: versionData, isLoading: isVersionLoading } = useQuery({
    queryKey: ['lesson-version', lessonId, versionNumber],
    queryFn: () => lessonService.getVersion(lessonId, versionNumber),
    enabled: !!lessonId,
  });

  const finalHtml = versionData?.final_html ?? currentVersion?.final_html;

  const assembledHtml = buildAssembledHtml(finalHtml, showNotes);

  useEffect(() => {
    if (iframeRef.current) {
      const doc = iframeRef.current.contentDocument;
      if (doc) {
        doc.open();
        doc.write(assembledHtml);
        doc.close();
      }
    }
  }, [assembledHtml]);

  const submitMutation = useMutation({
    mutationFn: () => lessonService.submit(lessonId, versionNumber),
    onSuccess: () => {
      setSubmitSuccess(true);
      setSubmitConfirmOpen(false);
    },
  });

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Main preview */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, bgcolor: '#1a1a1a', position: 'relative' }}>
          {isVersionLoading ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                flexDirection: 'column',
                gap: 2,
                color: 'white',
              }}
            >
              <CircularProgress color="inherit" />
              <Typography>Loading presentation...</Typography>
            </Box>
          ) : !finalHtml ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                flexDirection: 'column',
                gap: 2,
                color: 'grey.400',
              }}
            >
              <Typography variant="h6">No presentation HTML available</Typography>
              <Typography variant="body2">
                The backend will assemble slides when you save or submit.
              </Typography>
            </Box>
          ) : (
            <iframe
              ref={iframeRef}
              title="Lesson Presentation Preview"
              sandbox="allow-scripts allow-same-origin"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          )}
        </Box>

        {/* Submit footer */}
        <Box
          sx={{
            p: 2,
            bgcolor: 'white',
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {submitSuccess && (
              <Chip
                icon={<CheckCircleIcon />}
                label="Submitted for Review"
                color="success"
                variant="outlined"
              />
            )}
            {currentVersion?.submitted_at && (
              <Typography variant="caption" color="text.secondary">
                Submitted: {new Date(currentVersion.submitted_at).toLocaleDateString()}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Tooltip title="View version history">
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setHistoryOpen(true)}
              >
                History
              </Button>
            </Tooltip>

            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => setExportOpen(true)}
              disabled={!finalHtml}
            >
              Export
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={<SendIcon />}
              onClick={() => setSubmitConfirmOpen(true)}
              disabled={
                readOnly ||
                submitMutation.isPending ||
                submitSuccess ||
                !!currentVersion?.submitted_at
              }
            >
              {submitMutation.isPending ? (
                <CircularProgress size={20} />
              ) : (
                'Submit for Review'
              )}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Options panel */}
      <Paper
        elevation={0}
        square
        sx={{
          width: 260,
          flexShrink: 0,
          borderLeft: '1px solid',
          borderColor: 'divider',
          p: 2,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Presentation Options
        </Typography>

        <FormControlLabel
          control={
            <Switch checked={showNotes} onChange={(e) => setShowNotes(e.target.checked)} />
          }
          label="Show teacher notes"
        />

        <Divider />

        <Typography variant="subtitle2" fontWeight={600}>
          Version Info
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Version</Typography>
            <Typography variant="caption">v{versionNumber}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">Phase</Typography>
            <Typography variant="caption">Assembly</Typography>
          </Box>
          {currentVersion?.rejection_reason && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              <Typography variant="caption">
                <strong>Rejected:</strong> {currentVersion.rejection_reason}
              </Typography>
            </Alert>
          )}
        </Box>

        {submitMutation.isError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            Failed to submit. Please try again.
          </Alert>
        )}
      </Paper>

      {/* Submit confirmation dialog */}
      <Dialog open={submitConfirmOpen} onClose={() => setSubmitConfirmOpen(false)}>
        <DialogTitle>Submit for Review?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will submit the lesson for content review. You will not be able to edit it until
            it is reviewed. Are you sure?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => submitMutation.mutate()}
            disabled={submitMutation.isPending}
          >
            {submitMutation.isPending ? <CircularProgress size={20} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Slide export dialog */}
      {finalHtml && (
        <SlideExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          finalHtml={finalHtml}
          lessonTitle={currentLesson?.title}
        />
      )}

      {/* Version history dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Version History</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'primary.light',
                borderRadius: 1,
                bgcolor: 'primary.50',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2">Version {versionNumber} (Current)</Typography>
                <Chip label="Active" size="small" color="primary" />
              </Box>
              <Typography variant="caption" color="text.secondary">
                Created: {currentVersion ? new Date(currentVersion.created_at).toLocaleString() : 'N/A'}
              </Typography>
              {currentVersion?.submitted_at && (
                <Typography variant="caption" display="block" color="text.secondary">
                  Submitted: {new Date(currentVersion.submitted_at).toLocaleString()}
                </Typography>
              )}
              {currentVersion?.approved_at && (
                <Typography variant="caption" display="block" color="success.main">
                  Approved: {new Date(currentVersion.approved_at).toLocaleString()}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function buildAssembledHtml(finalHtml: string | undefined, showNotes: boolean): string {
  if (!finalHtml) {
    return `<!DOCTYPE html><html><body style="background:#1a1a1a;color:#aaa;font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;"><p>No presentation content available</p></body></html>`;
  }

  // Bespoke.js calls history.pushState which throws a SecurityError when the
  // document is loaded inside an iframe from a blob: URL.  Patch it early.
  const historyPatch = `<script>(function(){try{history.replaceState=function(){};history.pushState=function(){};}catch(e){}}());</script>`;

  // MARP puts teacher notes in <aside class="bespoke-marp-note">.
  // Bespoke.js hides them by default; inject CSS to show or hide them.
  const notesCss = showNotes
    ? `<style>aside.bespoke-marp-note{display:block!important;background:#fffde7;padding:8px 12px;font-size:13px;color:#333;margin-top:8px;border-top:2px solid #fbc02d;}</style>`
    : `<style>aside.bespoke-marp-note{display:none!important;}</style>`;

  const injection = historyPatch + notesCss;
  return finalHtml.includes('</head>')
    ? finalHtml.replace('</head>', injection + '</head>')
    : injection + finalHtml;
}
